/* global axios */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import api from '../services/apiService';
import { formatDateDE } from '../utils/dateFormatter';

function RentalCostRevenueReport() {
  // Filter
  const [startDate, setStartDate] = useState('');
  const [endDate,   setEndDate]   = useState('');
  const [productGroup, setProductGroup] = useState('');
  const [itemId, setItemId] = useState('');
  const [groupBy, setGroupBy] = useState('productGroup'); // 'productGroup' | 'item'
  const [includeAcquisition, setIncludeAcquisition] = useState(false);

  // Options
  const [productGroupOptions, setProductGroupOptions] = useState([]);
  const [itemOptions, setItemOptions] = useState([]); // nur rental + in PG

  // Auswahl-Objekte
  const selectedPG = useMemo(
    () => productGroup ? productGroupOptions.find(o => String(o.value) === String(productGroup)) ?? null : null,
    [productGroup, productGroupOptions]
  );
  const filteredItems = useMemo(() => {
    let list = itemOptions;
    if (productGroup) {
      list = list.filter(i => i.productGroupId === String(productGroup));
    }
    return list;
  }, [itemOptions, productGroup]);

  const selectedItem = useMemo(
    () => itemId ? filteredItems.find(o => String(o.value) === String(itemId)) ?? null : null,
    [itemId, filteredItems]
  );

  // Datenzustand
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [sumAll, setSumAll] = useState({
    invoiceCount: 0, billedDays: 0, revenueNet: 0, directCost: 0,
    margin: 0, marginRate: 0, avgDailyRate: 0, avgDailyCost: 0,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);

  // Details-Flag und expandierte Zeilen (pro Item-ID)
  const [includeDetails, setIncludeDetails] = useState(false);
  const [expandedRows, setExpandedRows] = useState(() => new Set());

  // Produktgruppen laden
  useEffect(() => {
    api.get('/product-groups')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : []).map(pg => ({
          value: String(pg.id), label: pg.name
        }));
        setProductGroupOptions(opts);
      })
      .catch(err => console.error('Produktgruppen laden fehlgeschlagen:', err));
  }, []);

  // Nur Items laden, die rental & in PG sind
  useEffect(() => {
    api.get('/items')
      .then(({ data }) => {
        const opts = (Array.isArray(data) ? data : [])
          .filter(i => i && i.itemType === 'rental' && i.productGroupId) // nur Mietartikel mit PG
          .map(i => ({
            id: i.id,
            value: String(i.id),
            label: `${i.name} (ID: ${i.id})`,
            productGroupId: String(i.productGroupId),
          }));
        setItemOptions(opts);
      })
      .catch(err => console.error('Items laden fehlgeschlagen:', err));
  }, []);

  // Request-Params
  const buildParams = useCallback(() => {
    const p = {};
    if (startDate)    p.dateFrom = startDate;
    if (endDate)      p.dateTo   = endDate;
    if (productGroup) p.productGroup = productGroup;
    if (itemId)       p.itemId       = itemId;
    if (groupBy)      p.groupBy      = groupBy;
    if (includeAcquisition) p.includeAcquisition = 'true';
    if (groupBy === 'item' && includeDetails) p.includeDetails = 'true';
    p.page = page;
    p.pageSize = pageSize;
    return p;
  }, [startDate, endDate, productGroup, itemId, groupBy, includeAcquisition, includeDetails, page, pageSize]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      setErrMsg('Bitte Start- und Enddatum wählen!');
      setRows([]); setTotal(0); setPages(1);
      setSumAll({ invoiceCount: 0, billedDays: 0, revenueNet: 0, directCost: 0, margin: 0, marginRate: 0, avgDailyRate: 0, avgDailyCost: 0 });
      return;
    }
    setLoading(true); setErrMsg('');
    try {
      const params = buildParams();
      const { data } = await api.get('/reports/rental/cost-revenue', { params });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setTotal(Number(data?.total || 0));
      setPages(Math.max(1, Number(data?.pages || 1)));
      setSumAll({
        invoiceCount: Number(data?.sumAll?.invoiceCount || 0),
        billedDays:   Number(data?.sumAll?.billedDays || 0),
        revenueNet:   Number(data?.sumAll?.revenueNet || 0),
        directCost:   Number(data?.sumAll?.directCost || 0),
        margin:       Number(data?.sumAll?.margin || 0),
        marginRate:   Number(data?.sumAll?.marginRate || 0),
        avgDailyRate: Number(data?.sumAll?.avgDailyRate || 0),
        avgDailyCost: Number(data?.sumAll?.avgDailyCost || 0),
      });
    } catch (e) {
      console.error('Rental Cost & Revenue Report laden fehlgeschlagen:', e);
      setErrMsg('Fehler beim Abrufen des Rental Cost & Revenue Reports');
      setRows([]); setTotal(0); setPages(1);
      setSumAll({ invoiceCount: 0, billedDays: 0, revenueNet: 0, directCost: 0, margin: 0, marginRate: 0, avgDailyRate: 0, avgDailyCost: 0 });
    } finally {
      setLoading(false);
    }
  }, [buildParams, startDate, endDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (page !== 1) setPage(1);
    else fetchData();
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchData();
  }, [page, fetchData, startDate, endDate]);

  useEffect(() => {
    setPage(1);
    setExpandedRows(new Set()); // Details zuklappen bei Filterwechsel
  }, [productGroup, itemId, groupBy, includeAcquisition, includeDetails, pageSize]);

  const handleDownloadCSV = async () => {
    if (!startDate || !endDate) {
      setErrMsg('Bitte Start- und Enddatum wählen!');
      return;
    }
    try {
      const params = { ...buildParams(), format: 'csv' };
      const response = await api.get('/reports/rental/cost-revenue', { params, responseType: 'blob' });
      const filename = `rental_cost_revenue_${groupBy}.csv`;
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV-Download fehlgeschlagen:', e);
      setErrMsg('CSV-Download fehlgeschlagen');
    }
  };

  // Tabelle
  const col1Label = groupBy === 'item' ? 'Item-ID' : 'PG-ID';
  const col2Label = groupBy === 'item' ? 'Maschine' : 'Produktgruppe';

  const gotoPrev = () => setPage(p => Math.max(1, p - 1));
  const gotoNext = () => setPage(p => Math.min(pages || 1, p + 1));

  const toggleExpand = (itemId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // Spaltenanzahl dynamisch (Header-Spalten zählen)
  // Basis (ohne Toggle, ohne die Ø-Tageskosten-Aufspaltung) = 10
  // Im Detailmodus ersetzen wir 1 Spalte ("Ø Tageskosten") durch 3 → +2 netto
  const isDetailMode = groupBy === 'item' && includeDetails;
  const baseCols = 10;
  const extraCols = isDetailMode ? 2 : 0;
  const hasToggleCol = isDetailMode;
  const totalCols = baseCols + extraCols + (hasToggleCol ? 1 : 0);

  return (
    <div style={{ maxWidth: 1200, margin: 'auto' }}>
      <Helmet>
        <title>Bericht: Maschinen Rentabilität</title>
      </Helmet>

      <h2>📊 Maschinen Rentabilität</h2>

      {/* Filter */}
      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Startdatum</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Enddatum</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 260px', minWidth: 260 }}>
            <label htmlFor="pgSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
            <Select
              inputId="pgSelect"
              isClearable
              placeholder="Produktgruppe wählen…"
              options={productGroupOptions}
              value={selectedPG}
              onChange={(opt) => { setProductGroup(opt ? String(opt.value) : ''); setItemId(''); setPage(1); }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={{
                control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
                container: (base) => ({ ...base, marginTop: -5 }),
                menuPortal:(base) => ({ ...base, zIndex: 9999 }),
                menu:      (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div style={{ flex: '1 1 320px', minWidth: 260 }}>
            <label htmlFor="itemSelect" style={{ display: 'inline-block', marginBottom: 4 }}></label>
            <Select
              inputId="itemSelect"
              isClearable
              placeholder={productGroup ? 'Mietartikel in dieser PG…' : 'Alle Mietartikel…'}
              options={filteredItems}
              value={selectedItem}
              onChange={(opt) => { setItemId(opt ? String(opt.value) : ''); setPage(1); }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={{
                control:   (base) => ({ ...base, minHeight: 34, width: '70%', marginBottom: 16 }),
                container: (base) => ({ ...base, marginTop: -5 }),
                menuPortal:(base) => ({ ...base, zIndex: 9999 }),
                menu:      (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 220 }}>
            <label>Gruppieren nach</label>
            <select value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setPage(1); }} style={{ width: '60%' }}>
              <option value="productGroup">Produktgruppe</option>
              <option value="item">Maschine</option>
            </select>
          </div>

          {/* Details anzeigen (nur im Maschinen-Modus) */}
          {groupBy === 'item' && (
            <div style={{ flex: '0 0 260px', minWidth: 220, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="includeDetails"
                type="checkbox"
                checked={includeDetails}
                onChange={e => setIncludeDetails(e.target.checked)}
              />
              <label htmlFor="includeDetails">Details anzeigen (Rechnungspositionen)</label>
            </div>
          )}

          {/* Option: Anschaffungskosten einrechnen */}
          <div style={{ flex: '0 0 260px', minWidth: 220, display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <input
              id="includeAcquisition"
              type="checkbox"
              checked={includeAcquisition}
              onChange={e => setIncludeAcquisition(e.target.checked)}
            />
            <label htmlFor="includeAcquisition">Anschaffungskosten einrechnen (einmalig)</label>
          </div>

          {/* Aktionen */}
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" onClick={handleDownloadCSV} disabled={loading}>CSV</button>
            
          </div>
        </div>
      </form>

      {errMsg && <p style={{ color: 'red' }}>{errMsg}</p>}

      {/* Summary-Bar */}
      <div style={{ marginBottom: 8 }}>
        <strong>Gesamt:</strong> {total} Gruppen &nbsp;|&nbsp;
        <strong>#Rechnungen:</strong> {sumAll.invoiceCount} &nbsp;|&nbsp;
        <strong>Tage:</strong> {Number(sumAll.billedDays).toLocaleString('de-DE')} &nbsp;|&nbsp;
        <strong>Umsatz:</strong> {Number(sumAll.revenueNet).toLocaleString('de-DE', { minimumFractionDigits: 2 })} € &nbsp;|&nbsp;
        <strong>Direkte Kosten:</strong> {Number(sumAll.directCost).toLocaleString('de-DE', { minimumFractionDigits: 2 })} € &nbsp;|&nbsp;
        <strong>Marge:</strong> {Number(sumAll.margin).toLocaleString('de-DE', { minimumFractionDigits: 2 })} € &nbsp;|&nbsp;
        <strong>Marge %:</strong> {(Number(sumAll.marginRate) * 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} %
      </div>

      {/* Pagination */}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button onClick={gotoPrev} disabled={loading || page <= 1}>Zurück</button>
        <span>Seite {page} / {pages || 1}</span>
        <button onClick={gotoNext} disabled={loading || page >= (pages || 1)}>Weiter</button>
      </div>

      {/* Tabelle */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fffefeff' }}>
              {/* Optionales Details-Icon nur im Item-Modus mit aktiven Details */}
              {isDetailMode && <th style={{ width: 46 }}></th>}
              <th style={{ textAlign: 'left', padding: 8 }}>{col1Label}</th>
              <th style={{ textAlign: 'left', padding: 8 }}>{col2Label}</th>
              <th style={{ textAlign: 'right', padding: 8 }}># Rechnungen</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Tage</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Umsatz (€)</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Direkte Kosten (€)</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Marge (€)</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Marge %</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Ø Tagessatz (€)</th>
              {isDetailMode ? (
                <>
                  <th style={{ textAlign: 'right', padding: 8 }}>Ø Tageskosten1 (€)</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>WA-Kosten (€)</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Ø Tageskosten2 (€)</th>
                </>
              ) : (
                <th style={{ textAlign: 'right', padding: 8 }}>Ø Tageskosten (€)</th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={totalCols} style={{ padding: 12, textAlign: 'center' }}>
                  Keine Daten
                </td>
              </tr>
            )}

            {rows.map(r => {
              const isItemModeWithDetails = isDetailMode;
              const isExpanded = isItemModeWithDetails && expandedRows.has(r.keyId);
              const hasDetails =
                isItemModeWithDetails &&
                r.details &&
                (Array.isArray(r.details.invoiceItems) || Array.isArray(r.details.workshopOrders));

              // Neue Durchschnittswerte nur im Detailmodus:
              const days    = Number(r.billedDays || 0);
              const direct  = Number(r.directCost || 0);
              const service = Number(r.serviceCost || 0); // nur im Item-Mode vorhanden
              const avgDailyCost1 = days > 0 ? (direct - service) / days : 0; // Ø ohne WA (unverändert)
              // WA-Kosten NICHT normalisieren:
              const waAmount      = service;                                  // Gesamtbetrag
              const avgDailyCost2 = days > 0 ?  direct / days : 0;            // Ø mit WA (unverändert)

              return (
                <React.Fragment key={`${r.keyId}-${r.keyName}`}>
                  {/* Hauptzeile */}
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    {isItemModeWithDetails && (
                      <td style={{ padding: 4, textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => toggleExpand(r.keyId)}
                          title={isExpanded ? 'Details einklappen' : 'Details ausklappen'}
                          style={{
                            width: 24,
                            height: 24,
                            border: '1px solid #999',
                            borderRadius: 4,
                            background: '#fff',
                            color: '#111',
                            cursor: 'pointer',
                            lineHeight: '22px',
                            textAlign: 'center',
                            padding: 0,
                            fontSize: 16,
                            fontWeight: 700,
                            userSelect: 'none',
                            opacity: hasDetails ? 1 : 0.5,
                            margin: 4
                          }}
                          disabled={!hasDetails}
                        >
                          {isExpanded ? '-' : '+'}
                        </button>
                      </td>
                    )}

                    <td style={{ padding: 8 }}>{r.keyId ?? '—'}</td>
                    <td style={{ padding: 8 }}>{r.keyName ?? '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{r.invoiceCount}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{Number(r.billedDays).toLocaleString('de-DE')}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.revenueNet).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.directCost).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.margin).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {(Number(r.marginRate) * 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} %
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.avgDailyRate).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>

                    {isDetailMode ? (
                      <>
                        {/* Ø Tageskosten1 (ohne WA) */}
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          {avgDailyCost1.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </td>
                        {/* Ø WA-Kosten */}
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          {waAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </td>
                        {/* Ø Tageskosten2 (mit WA) */}
                        <td style={{ padding: 8, textAlign: 'right' }}>
                          {avgDailyCost2.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </td>
                      </>
                    ) : (
                       <td style={{ padding: 8, textAlign: 'right' }}>
   {(
     Number(r.billedDays || 0) > 0
       ? Number(r.directCost || 0) / Number(r.billedDays || 0) // Ø Tageskosten (mit WA)
       : 0
   ).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
 </td>
                    )}
                  </tr>

                  {/* Detailzeile */}
                  {isItemModeWithDetails && isExpanded && hasDetails && (
                    <tr>
                      <td colSpan={totalCols} style={{ padding: 0 }}>
                        <div style={{ padding: 8, background: '#fafafa', borderTop: '1px solid #eee' }}>
                          {(() => {
                            // --- Details-Objekt robust normalisieren ---
                            const det = r.details || {};
                            const inv = Array.isArray(det.invoiceItems) ? det.invoiceItems : [];
                            const wos = Array.isArray(det.workshopOrders) ? det.workshopOrders : [];
                            const serviceSum = Number(det.serviceCostSum || 0);

                            if (inv.length === 0 && wos.length === 0) {
                              return (
                                <div style={{ padding: 8, textAlign: 'center', opacity: .7 }}>
                                  Keine Details im Zeitraum.
                                </div>
                              );
                            }

                            return (
                              <>
                                {/* --- Rechnungspositionen (InvoiceItems) --- */}
<div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 6px 0' }}>
  <span style={{ fontWeight: 600 }}>Rechnungspositionen</span>
</div>

{inv.length === 0 ? (
  <div style={{ padding: 6, opacity: 0.8 }}>Keine Rechnungspositionen im Zeitraum.</div>
) : (
  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
    <thead>
      <tr>
        <th style={{ textAlign: 'left',  padding: 6 }}>Rechnung</th>
        <th style={{ textAlign: 'left',  padding: 6 }}>Datum</th>
        <th style={{ textAlign: 'right', padding: 6 }}>Menge</th>
        <th style={{ textAlign: 'left',  padding: 6 }}>Einheit</th>
        <th style={{ textAlign: 'right', padding: 6 }}>Umsatz (€)</th>
        <th style={{ textAlign: 'right', padding: 6 }}>Var. Kosten (€)</th>
         <th style={{ textAlign: 'right', padding: 6 }}>Fix: Versicherung (€)</th>
 <th style={{ textAlign: 'right', padding: 6 }}>Fix: Finanzierung (€)</th>
 <th style={{ textAlign: 'right', padding: 6 }}>Fix: KFZ (€)</th>
 <th style={{ textAlign: 'right', padding: 6 }}>Fix: Summe (€)</th>
      </tr>
    </thead>
    <tbody>
      {inv.map((d, idx) => {
        const dateDE = formatDateDE(d.invoiceDate, '—');

        return (
          <tr key={`${r.keyId}-det-${idx}`} style={{ borderTop: '1px solid #eee' }}>
            <td style={{ padding: 6 }}>{d.invoiceNumber || d.invoiceId}</td>
            <td style={{ padding: 6 }}>{dateDE}</td>
            <td style={{ padding: 6, textAlign: 'right' }}>{d.quantity}</td>
            <td style={{ padding: 6 }}>{d.unit || '—'}</td>
            <td style={{ padding: 6, textAlign: 'right' }}>
              {Number(d.revenueNet).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </td>
            <td style={{ padding: 6, textAlign: 'right' }}>
              {Number(d.variableCost).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </td>
            <td style={{ padding: 6, textAlign: 'right' }}>
   {Number(d.fixedCostShareInsurance || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right' }}>
   {Number(d.fixedCostShareFinancing || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right' }}>
   {Number(d.fixedCostShareKfz || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right' }}>
   {Number(d.fixedCostShare || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
          </tr>
        );
      })}
    </tbody>

    {/* Summen-Zeile */}
    <tfoot>
      {(() => {
        const sumRevenue = inv.reduce((a, d) => a + Number(d.revenueNet || 0), 0);
        const sumVar     = inv.reduce((a, d) => a + Number(d.variableCost || 0), 0);
        const sumFixIns  = inv.reduce((a, d) => a + Number(d.fixedCostShareInsurance || 0), 0);
const sumFixFin  = inv.reduce((a, d) => a + Number(d.fixedCostShareFinancing || 0), 0);
const sumFixKfz  = inv.reduce((a, d) => a + Number(d.fixedCostShareKfz || 0), 0);
const sumFix     = inv.reduce((a, d) => a + Number(d.fixedCostShare || 0), 0);

        return (
          <tr style={{ borderTop: '2px solid #ddd', background: '#fff' }}>
            <td colSpan={4} style={{ padding: 6, fontWeight: 700, textAlign: 'right' }}>Summe:</td>
            <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
              {sumRevenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </td>
            <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
              {sumVar.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
            </td>
            <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
   {sumFixIns.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
   {sumFixFin.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
   {sumFixKfz.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
 <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
   {sumFix.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
 </td>
          </tr>
        );
      })()}
    </tfoot>
  </table>
)}



                                {/* --- Werkstattaufträge (nicht berechnet) --- */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 6px 0' }}>
                                  <span style={{ fontWeight: 600 }}>
                                    Werkstattaufträge (nicht berechnet) – Summe:{' '}
                                    {serviceSum.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                                  </span>
                                </div>

                                {wos.length === 0 ? (
                                  <div style={{ padding: 6, opacity: 0.8 }}>Keine Werkstattaufträge im Zeitraum.</div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                       <th style={{ textAlign: 'left',  padding: 6 }}>WO-Nr.</th>
                                        <th style={{ textAlign: 'left',  padding: 6 }}>Datum</th>
                                        <th style={{ textAlign: 'right', padding: 6 }}>Servicekosten (Netto)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {wos.map((w, idx) => (
                                        <tr key={`${r.keyId}-wo-${idx}`} style={{ borderTop: '1px solid #eee' }}>
                                          <td style={{ padding: 6 }}>{w.woNumber || w.woId}</td>
                                          <td style={{ padding: 6 }}>{formatDateDE(w.date, '—')}</td>
                                          <td style={{ padding: 6, textAlign: 'right' }}>
                                            {Number(w.serviceCost).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                                          </td>
                                        </tr>
                                      ))}
                                      <tr style={{ borderTop: '2px solid #ddd', background: '#fff' }}>
                                        <td style={{ padding: 6, fontWeight: 700 }}>Summe WOs</td>
                                        <td></td>
                                        <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
                                          {serviceSum.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && <p style={{ marginTop: 8 }}>Lade…</p>}
    </div>
  );
}

export default RentalCostRevenueReport;
